import { IUserProfile } from "@redbelly-tokeniser/mystake-api-sdk";
import { IInvestorProfiles, IInvestorRequest, Roles, ShareholderStatus } from "@redbelly-tokeniser/mystake-common-sdk";
import { InvestorType } from "@redbelly-tokeniser/mystake-validator-public";
import React, { useEffect, useState, PropsWithChildren } from "react";
import { useLocation, useRouteMatch } from "react-router-dom";
import { Order } from "../constants/constants";
import { authService, companyService, userService } from "../util";
import { analyticsClient } from "../analytics/client";

interface AppContextInterface {
  currentUser: IUserProfile | null;
  updateUser: () => Promise<void>;
  invalidateCurrentUser: () => void;
  loader: boolean;
  setIsInvestor: (isInvestor: boolean) => void;
  /**
   * External investors are external users who have signed in to create an investor profile.
   *
   * The role is assigned in the AuthProvider to account for users bypassing the onboarding flow
   * and to share the state with other providers on page load.
   */
  setExternalInvestor: (acn: string, companyId?: string) => Promise<void>;
  getUpdatedUser: () => Promise<IUserProfile | null>;
  getInvestors: (
    companyId: string,
    page?: number,
    rowsPerPage?: number,
    sortBy?: string,
    sortOrder?: Order,
    keyword?: string,
    filterByCompany?: boolean,
    status?: ShareholderStatus[]
  ) => Promise<IInvestorProfiles | null>;
}

export const AuthContext = React.createContext<AppContextInterface>({
  currentUser: null,
  updateUser: async (): Promise<void> => {},
  invalidateCurrentUser: () => {},
  loader: true,
  setIsInvestor: () => {},
  setExternalInvestor: async (): Promise<void> => {},
  getUpdatedUser: async (): Promise<IUserProfile | null> => null,
  getInvestors: async (): Promise<IInvestorProfiles | null> => null,
});

/** The signed-in Averer account the wallet is bound to; null for password sessions. */
export const AvererAccountIdContext = React.createContext<string | null>(null);

export const useAvererAccountId = (): string | null => React.useContext(AvererAccountIdContext);

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<IUserProfile | null>(null);
  const [avererAccountId, setAvererAccountId] = useState<string | null>(null);
  const [loader, showLoader] = useState(true);
  const [isInvestor, setIsInvestor] = useState(false);
  const [isExternalInvestor, setIsExternalInvestor] = useState<boolean>(false);

  const location = useLocation();
  const matchSignin = useRouteMatch("/signin");
  let acnParam: string | null = null;

  if (matchSignin && location.search) {
    const queryParams = new URLSearchParams(location.search.toLowerCase());
    // Emails for external users are verified during sign in
    acnParam = queryParams.get("acn");
  }

  useEffect(() => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        isInvestor,
        isExternalInvestor,
      });
    }
  }, [isInvestor, isExternalInvestor, setCurrentUser]);

  useEffect(() => {
    if (acnParam) {
      setExternalInvestor(acnParam);
    }
  }, [acnParam]);

  // Clearing the user means the session is signed out (or never established). Reset analytics
  // too, so Mixpanel drops the previous identify and stops attributing events to the old user.
  const clearUser = React.useCallback(() => {
    setCurrentUser(null);
    setAvererAccountId(null);
    analyticsClient.reset();
  }, [setCurrentUser]);

  // An Averer sign-in completed in-app lands here before any reload, so the account is read again.
  const refreshAvererAccountId = async (): Promise<void> => {
    try {
      const sessionUser = await authService.currentUser();
      setAvererAccountId(sessionUser?.avererAccountId ?? null);
    } catch {
      setAvererAccountId(null);
    }
  };

  const updateUser = async (): Promise<void> => {
    try {
      if (!currentUser) {
        await refreshAvererAccountId();
        await setUser();
      } else {
        const userProfile = await userService.getUser();
        if (!userProfile) {
          throw new Error("Unable to load user profile");
        }
        userProfile.isInvestor = isInvestor;
        userProfile.isExternalInvestor = isExternalInvestor;
        setCurrentUser(userProfile);
      }
    } catch {
      clearUser();
    }
    showLoader(false);
  };

  const getUpdatedUser = React.useCallback(async (): Promise<IUserProfile | null> => {
    try {
      const userProfile = await userService.getUser();
      userProfile.isInvestor = isInvestor;
      userProfile.isExternalInvestor = isExternalInvestor;
      return userProfile;
    } catch {
      clearUser();
      return null;
    }
  }, [isInvestor, isExternalInvestor, clearUser]);

  const setUser = async (): Promise<void> => {
    try {
      const userProfile = await userService.getUser();
      const isAdmin = !!userProfile.roles.includes(Roles.ADMIN);
      const isApprover = !!userProfile.roles.includes(Roles.APPROVER);
      const isExternal = !!userProfile.roles.includes(Roles.EXTERNAL_INVESTOR);
      setIsInvestor(!isAdmin && !isApprover);
      userProfile.isInvestor = !isAdmin && !isApprover;
      setIsExternalInvestor(isExternal);
      userProfile.isExternalInvestor = isExternal;
      setCurrentUser(userProfile);
      analyticsClient.identify(userProfile._id);
    } catch {
      clearUser();
    }
    showLoader(false);
  };

  const invalidateCurrentUser = async () => {
    clearUser();
  };

  useEffect(() => {
    const fetchCurrentUser = async () => {
      const user = await authService.currentUser();
      setAvererAccountId(user?.avererAccountId ?? null);
      if (user) {
        setUser();
      } else {
        clearUser();
        showLoader(false);
      }
    };
    fetchCurrentUser();
  }, []);

  const setExternalInvestor = async (acn: string, companyId?: string): Promise<void> => {
    try {
      const userProfile = await userService.getUser();
      const shareholdings = userProfile?.shareholdings || [];
      const onboardingCompanyId = companyId || (await companyService.getCompanyByAcn(acn)).payload;
      const hasHoldingsInCompany = shareholdings.length > 0 && shareholdings[0].companyId === onboardingCompanyId;

      if (!hasHoldingsInCompany) {
        const grantedRole = await userService.grantShareholderRole(onboardingCompanyId, Roles.EXTERNAL_INVESTOR);
        // eslint-disable-next-line no-unused-expressions
        grantedRole && setIsExternalInvestor(true);
      }
    } catch {
      setIsExternalInvestor(false);
    }
    showLoader(false);
  };

  const getInvestors = React.useCallback(
    async (
      companyId: string,
      page = 1,
      rowsPerPage = 10,
      sortBy = "legalEntityName",
      sortOrder: Order = "asc",
      keyword = "",
      filterByCompany = false,
      status: ShareholderStatus[] = [ShareholderStatus.ACCEPTED]
    ): Promise<IInvestorProfiles | null> => {
      try {
        const response = await userService.getInvestorProfiles(
          companyId,
          filterByCompany,
          status,
          keyword,
          page,
          rowsPerPage,
          sortBy,
          sortOrder
        );

        const sophisticatedInvestors = response.payload.investors.filter(
          (investor: IInvestorRequest) => investor.investorType === InvestorType.SOPHISTICATED
        );
        const existingInvestors: IInvestorProfiles = {
          count: sophisticatedInvestors.length,
          investors: sophisticatedInvestors,
        };
        return existingInvestors;
      } catch {
        return null;
      }
    },
    []
  );

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        updateUser,
        invalidateCurrentUser,
        loader,
        setIsInvestor,
        getUpdatedUser,
        setExternalInvestor,
        getInvestors,
      }}
    >
      <AvererAccountIdContext.Provider value={avererAccountId}>{children}</AvererAccountIdContext.Provider>
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => React.useContext(AuthContext);

/**
 * True when the signed-in account is linked to Averer, which then owns the email address.
 * Consumers use it to hide Tokeniser-side email editing and re-verification.
 */
export const useIsAvererLinked = (): boolean => {
  const { currentUser } = React.useContext(AuthContext);
  return Boolean(currentUser?.avererAccountId);
};

/**
 * Single voice for every Tokeniser-side surface that refuses an email change because Averer owns
 * the address (buttons, toasts, inline errors). Keeps the wording in sync across every consumer
 * instead of each one defining its own copy of the same string.
 */
export const AVERER_MANAGED_EMAIL_MESSAGE =
  "Your email address is managed at Averer and cannot be changed in Tokeniser.";
